import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSubscription } from '../context/SubscriptionContext';
import { useAuth } from '../context/AuthContext';
import { X } from 'lucide-react';
import { Button } from './ui/button';

/**
 * ConversionPopups - Handles 3 conversion triggers for free trial users
 * 
 * Trigger 1: After first mind map is generated (30s delay)
 * Trigger 2: After 3+ minutes of active engagement on MindMapPage
 * Trigger 3: When free trial limit is hit (free map used)
 */

// Popup wrapper component with consistent styling
const PopupModal = ({ isOpen, onClose, children, isSlideUp = false, isUrgent = false }) => {
  if (!isOpen) return null;

  if (isSlideUp) {
    // Slide-up from bottom right (Trigger 2)
    return (
      <div 
        className="fixed bottom-6 right-6 z-50 animate-in slide-in-from-bottom-5 fade-in duration-300"
        style={{ maxWidth: '380px' }}
      >
        <div className="bg-[#FDF8F0] dark:bg-[#2a2f3e] rounded-xl shadow-2xl border border-border overflow-hidden">
          {children}
        </div>
      </div>
    );
  }

  // Full modal (Trigger 1 & 3)
  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-in fade-in duration-300"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div 
        className={`relative w-full max-w-md bg-[#FDF8F0] dark:bg-[#2a2f3e] rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300 ${isUrgent ? 'border-2 border-orange-400' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
};

// Trigger 1: First Mind Map Popup
const FirstMapPopup = ({ isOpen, onClose, onUpgrade, userName, mapsRemaining }) => (
  <PopupModal isOpen={isOpen} onClose={onClose}>
    <div className="p-6 text-center space-y-4">
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-2 rounded-full hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
        aria-label="Close popup"
      >
        <X className="w-5 h-5 text-muted-foreground" />
      </button>
      
      <div className="text-4xl">🎉</div>
      
      <h2 className="text-xl font-serif font-semibold text-foreground">
        Your first mind map is ready{userName ? `, ${userName}` : ''}!
      </h2>
      
      <p className="text-muted-foreground">
        You have {mapsRemaining} map{mapsRemaining !== 1 ? 's' : ''} remaining on your free trial. 
        Go Pro for up to 50 maps/month and 30 AI credits.
      </p>
      
      <div className="flex flex-col gap-3 pt-2">
        <Button
          onClick={onUpgrade}
          className="w-full h-12 text-base font-semibold rounded-full"
          style={{ backgroundColor: '#2D6A4F' }}
          data-testid="first-map-go-pro-btn"
        >
          Go Pro
        </Button>
        <Button
          onClick={onClose}
          variant="ghost"
          className="w-full text-muted-foreground hover:text-foreground"
          data-testid="first-map-dismiss-btn"
        >
          Keep Exploring
        </Button>
      </div>
    </div>
  </PopupModal>
);

// Trigger 2: Engagement Popup (slide-up)
const EngagementPopup = ({ isOpen, onClose, onUpgrade, userName }) => (
  <PopupModal isOpen={isOpen} onClose={onClose} isSlideUp>
    <div className="p-5 space-y-3">
      <div className="flex justify-between items-start">
        <h3 className="text-lg font-serif font-semibold text-foreground pr-8">
          You're getting it{userName ? `, ${userName}` : ''}.
        </h3>
        <button
          onClick={onClose}
          className="p-1.5 rounded-full hover:bg-black/5 dark:hover:bg-white/10 transition-colors flex-shrink-0"
          aria-label="Close popup"
        >
          <X className="w-4 h-4 text-muted-foreground" />
        </button>
      </div>
      
      <p className="text-sm text-muted-foreground">
        Visual learning sticks. Upgrade to Pro for up to 50 maps/month and 30 AI credits.
      </p>
      
      <div className="flex gap-2 pt-1">
        <Button
          onClick={onUpgrade}
          className="flex-1 h-10 text-sm font-semibold rounded-full"
          style={{ backgroundColor: '#2D6A4F' }}
          data-testid="engagement-upgrade-btn"
        >
          Try Pro for $9.99/month
        </Button>
        <Button
          onClick={onClose}
          variant="outline"
          className="h-10 text-sm rounded-full px-4"
          data-testid="engagement-dismiss-btn"
        >
          Maybe Later
        </Button>
      </div>
    </div>
  </PopupModal>
);

// Trigger 3: Limit Hit Popup (urgent)
const LimitHitPopup = ({ isOpen, onClose, onUpgrade }) => (
  <PopupModal isOpen={isOpen} onClose={onClose} isUrgent>
    <div className="p-6 text-center space-y-4">
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-2 rounded-full hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
        aria-label="Close popup"
      >
        <X className="w-5 h-5 text-muted-foreground" />
      </button>
      
      <div className="w-16 h-16 mx-auto bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center">
        <svg className="w-8 h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      </div>
      
      <h2 className="text-xl font-serif font-semibold text-foreground">
        You've used your free map
      </h2>
      
      <p className="text-muted-foreground">
        Ready to keep learning?
      </p>
      
      <div className="flex flex-col gap-3 pt-2">
        <Button
          onClick={onUpgrade}
          className="w-full h-12 text-base font-semibold rounded-full bg-orange-500 hover:bg-orange-600"
          data-testid="limit-hit-upgrade-btn"
        >
          Upgrade Now
        </Button>
        <Button
          onClick={onClose}
          variant="ghost"
          className="w-full text-muted-foreground hover:text-foreground"
          data-testid="limit-hit-dismiss-btn"
        >
          Not Yet
        </Button>
      </div>
    </div>
  </PopupModal>
);

// Main ConversionPopups component
export function ConversionPopups() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { subscription, isTrialActive, isSubscribed, getRemainingMindMaps } = useSubscription();
  
  const [showFirstMapPopup, setShowFirstMapPopup] = useState(false);
  const [showEngagementPopup, setShowEngagementPopup] = useState(false);
  const [showLimitHitPopup, setShowLimitHitPopup] = useState(false);
  
  const activeTimeRef = useRef(0);
  const engagementTimerRef = useRef(null);
  const firstMapTimerRef = useRef(null);

  // Get user's first name
  const userName = user?.name?.split(' ')[0] || '';
  const mapsRemaining = getRemainingMindMaps();

  // Check if user is on free trial (not Pro)
  const isFreeTrial = isTrialActive && !isSubscribed;

  // Navigate to subscription page
  const handleUpgrade = useCallback(() => {
    setShowFirstMapPopup(false);
    setShowEngagementPopup(false);
    setShowLimitHitPopup(false);
    navigate('/subscription');
  }, [navigate]);

  // Dismiss handlers with localStorage persistence
  const dismissFirstMapPopup = useCallback(() => {
    setShowFirstMapPopup(false);
    localStorage.setItem('popup_first_map_dismissed', 'true');
  }, []);

  const dismissEngagementPopup = useCallback(() => {
    setShowEngagementPopup(false);
    localStorage.setItem('popup_engagement_dismissed', 'true');
  }, []);

  const dismissLimitHitPopup = useCallback(() => {
    setShowLimitHitPopup(false);
    localStorage.setItem('popup_limit_dismissed', 'true');
  }, []);

  // Trigger 1: First mind map popup (30s after map loads)
  useEffect(() => {
    // Only for free trial users on MindMapPage
    if (!isFreeTrial) return;
    if (!location.pathname.startsWith('/mindmap/')) return;
    if (localStorage.getItem('popup_first_map_dismissed')) return;
    
    // Check if this is their first map (maps_used === 1)
    const mapsUsed = subscription?.mind_maps_used;
    if (mapsUsed !== 1) return;

    // Wait 30 seconds before showing
    firstMapTimerRef.current = setTimeout(() => {
      setShowFirstMapPopup(true);
    }, 30000);

    return () => {
      if (firstMapTimerRef.current) {
        clearTimeout(firstMapTimerRef.current);
      }
    };
  }, [isFreeTrial, location.pathname, subscription?.mind_maps_used]);

  // Trigger 2: Engagement popup (3+ minutes of active time on MindMapPage)
  useEffect(() => {
    // Only for free trial users on MindMapPage
    if (!isFreeTrial) return;
    if (!location.pathname.startsWith('/mindmap/')) return;
    if (localStorage.getItem('popup_engagement_dismissed')) return;
    // Don't show if already shown this session
    if (sessionStorage.getItem('popup_engagement_shown_session')) return;

    let isVisible = true;
    activeTimeRef.current = 0;

    // Track visibility changes
    const handleVisibilityChange = () => {
      isVisible = document.visibilityState === 'visible';
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Increment active time every second when page is visible
    engagementTimerRef.current = setInterval(() => {
      if (isVisible && !showEngagementPopup) {
        activeTimeRef.current += 1;
        
        // Show popup after 3 minutes (180 seconds)
        if (activeTimeRef.current >= 180) {
          setShowEngagementPopup(true);
          sessionStorage.setItem('popup_engagement_shown_session', 'true');
          clearInterval(engagementTimerRef.current);
        }
      }
    }, 1000);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (engagementTimerRef.current) {
        clearInterval(engagementTimerRef.current);
      }
    };
  }, [isFreeTrial, location.pathname, showEngagementPopup]);

  // Trigger 3: Listen for limit hit event
  useEffect(() => {
    if (!isFreeTrial) return;

    const handleLimitHit = () => {
      setShowLimitHitPopup(true);
    };

    // Listen for custom event from mind map generation
    window.addEventListener('mindmap-limit-hit', handleLimitHit);

    return () => {
      window.removeEventListener('mindmap-limit-hit', handleLimitHit);
    };
  }, [isFreeTrial]);

  // Also check subscription data for limit hit
  useEffect(() => {
    if (!isFreeTrial) return;
    if (localStorage.getItem('popup_limit_dismissed')) return;

    const mapsUsed = subscription?.mind_maps_used || 0;
    const mapsLimit = subscription?.mind_maps_limit;

    // If they've hit the limit, show the popup
    if (mapsUsed >= mapsLimit && mapsLimit > 0) {
      // Small delay to ensure it shows after any navigation
      const timer = setTimeout(() => {
        setShowLimitHitPopup(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isFreeTrial, subscription?.mind_maps_used, subscription?.mind_maps_limit]);

  // Don't render anything for Pro users
  if (isSubscribed || !isTrialActive) return null;

  return (
    <>
      <FirstMapPopup
        isOpen={showFirstMapPopup}
        onClose={dismissFirstMapPopup}
        onUpgrade={handleUpgrade}
        userName={userName}
        mapsRemaining={mapsRemaining}
      />
      <EngagementPopup
        isOpen={showEngagementPopup}
        onClose={dismissEngagementPopup}
        onUpgrade={handleUpgrade}
        userName={userName}
      />
      <LimitHitPopup
        isOpen={showLimitHitPopup}
        onClose={dismissLimitHitPopup}
        onUpgrade={handleUpgrade}
      />
    </>
  );
}

// Export a function to trigger limit hit popup from other components
export const triggerLimitHitPopup = () => {
  window.dispatchEvent(new CustomEvent('mindmap-limit-hit'));
};

export default ConversionPopups;
